import { property } from '@wonderlandengine/api/decorators.js';
import { Component, Material, MeshComponent, NumberArray, Object3D, TextComponent } from '@wonderlandengine/api';
import { ColourDescription } from './components/colour-description.js';
import { GrilleDescription } from './components/grille-description.js';
import { PaintDescription } from './components/paint-description.js';
import { MaterialDescription } from './components/material-description.js';
import { Utilities } from '../utilities/utilities.js';
import { Easing, Tween } from '@tweenjs/tween.js';
import { TweenManager } from '../utilities/tween-manager.js';
import { Configuration, SpareWheelOptions, AccentOptions } from '../data/configuration.js';
import { CarPbrMaterial, cloneBodyPaintMaterial, clonePaintMaterial } from './paint-configurator.ts'

//-----------------------------------------------------------------------------------------
// Type Definitions:
//-----------------------------------------------------------------------------------------

interface HoodMaterial extends Material {
	hoodColor: NumberArray;
	pipeColor: NumberArray;
	logoColor: NumberArray;
}

interface GrilleMaterial extends Material {
	albedoColor: NumberArray;
}

type FadableMaterial = Material | GrilleMaterial | CarPbrMaterial;
type FadableKey = {
	[TK in keyof FadableMaterial]: FadableMaterial[TK] extends NumberArray ? TK : never;
}[keyof FadableMaterial];

//-----------------------------------------------------------------------------------------
// Constants:
//-----------------------------------------------------------------------------------------

const COLOUR_TWEEN_TIME_IN_MS = 250;

/**
 * Exterior Configurator:
 */
export class ExteriorConfigurator extends Component {
	static TypeName = 'exterior-configurator';

	//-----------------------------------------------------------------------------------------
	// Inspector Variables:
	//-----------------------------------------------------------------------------------------

	@property.object({ required: true })
	hoodDescriptions!: Object3D;

	@property.object({ required: true })
	softTopUp!: Object3D;

	@property.object({ required: true })
	softTopDown!: Object3D;

	@property.object({ required: true })
	softTopDownLight!: Object3D;

	@property.object({ required: true })
	softTopUpLight!: Object3D;

	@property.object({ required: true })
	softTopUp4Wheel!: Object3D;

	@property.object({ required: true })
	softTopUp5Wheel!: Object3D; 

	@property.object({ required: true })
	softTopDown4Wheel!: Object3D;

	@property.object({ required: true })
	softTopDown5Wheel!: Object3D;

	@property.object({ required: true })
	sideWindows!: Object3D;

	@property.object({ required: true })
	exteriorSideScreen!: Object3D;

	@property.object({ required: true })
	wingBeadingDescriptions!: Object3D;

	@property.object({ required: true })
	wingBeading!: Object3D;

	@property.object({ required: true })
	licensePlate!: Object3D;

	@property.object({ required: true })
	licensePlateUsFront!: Object3D;

	@property.object({ required: true })
	licensePlateUsBack!: Object3D;

	@property.object({ required: true })
	stoneGuard!: Object3D;

	@property.object({ required: true })
	stoneGuardDescriptions!: Object3D;

	@property.object({ required: true })
	grille!: Object3D;

	@property.object({ required: true })
	lowerGrill!: Object3D;

	@property.object({ required: true })
	grilleDescriptions!: Object3D;

	@property.object({ required: true })
	splitterDiffuserCarPaint!: Object3D;

	@property.object({ required: true })
	splitterDiffuserCarPaintRear!: Object3D;

	@property.object({ required: true })
	splitterDiffuserBlack!: Object3D;

	@property.object({ required: true })
	paintDescriptions!: Object3D;

	@property.object({ required: true })
	doorRoundelDescriptions!: Object3D;

	@property.object({ required: true })
	doorRoundel!: Object3D;

	@property.object({ required: true })
	badgeFlagColoured!: Object3D;
	
	@property.object({ required: true })
	badgeFlagBlack!: Object3D;
	
	@property.object({ required: true })
	windowSides!: Object3D;

	@property.object({ required: true })
	sideWindowFrames!: Object3D;

	@property.object({ required: true })
	callSignPilotBadge!: Object3D;

	@property.object({ required: true })
	callSignPilotText!: Object3D;

	@property.object({ required: true })
	callSignCopilotBadge!: Object3D;

	@property.object({ required: true })
	callSignCopilotText!: Object3D;

	@property.object({ required: true })
	fuelCapUsa!: Object3D;

	@property.material({ required: true })
	accentPackmaterial: Material | null = null;
	
	@property.color()
	accentPackColour!: NumberArray;

	@property.color()
	chromeColour!: NumberArray;

	//-----------------------------------------------------------------------------------------
	// Fields:
	//-----------------------------------------------------------------------------------------

	// component references.
	private _hoodDescriptions!: ColourDescription[];
	private _softTopUp4Wheel!: MeshComponent;
	private _softTopUp5Wheel!: MeshComponent;
	private _softTopDown4Wheel!: MeshComponent;
	private _softTopDown5Wheel!: MeshComponent;
	private _exteriorSideScreen!: MeshComponent;
	private _wingBeadingDescriptions!: ColourDescription[];
	private _wingBeading!: MeshComponent;
	private _stoneGuardDescriptions!: ColourDescription[];
	private _stoneGuard!: MeshComponent;
	private _grilleDescriptions!: GrilleDescription[];
	private _grille!: MeshComponent;
	private _lowerGrill!: MeshComponent;
	private _splitterDiffuserCarPaint!: MeshComponent;
	private _splitterDiffuserCarPaintRear!: MeshComponent;

	// the material of each half of the splitter while it is wearing a bespoke colour - the rear the body's, the front the wings' -
	// kept so that the colour can be written straight onto it as the picker is dragged rather than cloning and tweening on every
	// move. null whenever that half is wearing a standard paint.
	private _bespokeRearSplitterMaterial: CarPbrMaterial | null = null;
	private _bespokeFrontSplitterMaterial: CarPbrMaterial | null = null;
	private _splitterDiffuserBlack!: MeshComponent;
	private _paintDescriptions!: PaintDescription[];
	private _doorRoundelDescriptions!: MaterialDescription[];
	private _doorRoundel!: MeshComponent;
	private _windowSides!: MeshComponent;
	private _sideWindowFrames!: MeshComponent;
	private _callSignPilotBadge!: MeshComponent;
	private _callSignCopilotBadge!: MeshComponent;
	private _callSignPilotText!: TextComponent;
	private _callSignCopilotText!: TextComponent;

	private _tweenManager = new TweenManager();
	
	//-----------------------------------------------------------------------------------------
	// Lifecycle Functions:
	//-----------------------------------------------------------------------------------------

	start() {

		// get components.
		this._hoodDescriptions = this.hoodDescriptions.getComponents(ColourDescription)!;
		this._softTopUp4Wheel = this.softTopUp4Wheel.getComponent(MeshComponent)!;
		this._softTopUp5Wheel = this.softTopUp5Wheel.getComponent(MeshComponent)!;
		this._softTopDown4Wheel = this.softTopDown4Wheel.getComponent(MeshComponent)!;
		this._softTopDown5Wheel = this.softTopDown5Wheel.getComponent(MeshComponent)!;
		this._exteriorSideScreen = this.exteriorSideScreen.getComponent(MeshComponent)!;
		this._wingBeadingDescriptions = this.wingBeadingDescriptions.getComponents(ColourDescription)!;
		this._wingBeading = this.wingBeading.getComponent(MeshComponent)!;
		this._stoneGuardDescriptions = this.stoneGuardDescriptions.getComponents(ColourDescription)!;
		this._stoneGuard = this.stoneGuard.getComponent(MeshComponent)!;
		this._grilleDescriptions = this.grilleDescriptions.getComponents(GrilleDescription)!;
		this._grille = this.grille.getComponent(MeshComponent)!;
		this._lowerGrill = this.lowerGrill.getComponent(MeshComponent)!;
		this._splitterDiffuserCarPaint = this.splitterDiffuserCarPaint.getComponent(MeshComponent)!;
		this._splitterDiffuserCarPaintRear = this.splitterDiffuserCarPaintRear.getComponent(MeshComponent)!;
		this._splitterDiffuserBlack = this.splitterDiffuserBlack.getComponent(MeshComponent)!;
		this._paintDescriptions = this.paintDescriptions.getComponents(PaintDescription)!;
		this._doorRoundelDescriptions = this.doorRoundelDescriptions.getComponents(MaterialDescription)!;
		this._doorRoundel = this.doorRoundel.getComponent(MeshComponent)!;
		this._windowSides = this.windowSides.getComponent(MeshComponent)!;
		this._sideWindowFrames = this.sideWindowFrames.getComponent(MeshComponent)!;
		this._callSignPilotBadge = this.callSignPilotBadge.getComponent(MeshComponent)!;
		this._callSignCopilotBadge = this.callSignCopilotBadge.getComponent(MeshComponent)!;
		this._callSignPilotText = this.callSignPilotText.getComponent(TextComponent)!;
		this._callSignCopilotText = this.callSignCopilotText.getComponent(TextComponent)!;
	}

	update() {
		this._tweenManager.update();
	}

	//-----------------------------------------------------------------------------------------
	// Public Functions:
	//-----------------------------------------------------------------------------------------

	/**
	* Selects the soft top state, putting it up or down by updating the relevant meshes/materials on the vehicle model.
	*/
	selectSoftTopState(setHoodUp: boolean) {
		Utilities.setActiveAll(this.softTopUp, setHoodUp);
		Utilities.setActiveAll(this.softTopDown, !setHoodUp);

		this.selectSoftTopLightState(!this._spareWheelIsActive());
	}
	
	/**
	* Selects the side window state, putting them up or down by updating the relevant meshes/materials on the vehicle model.
	*/
	selectSideWindowState(setWindowsUp: boolean) {
		Utilities.setActiveAll(this.sideWindows, setWindowsUp);
	}
	
	/**
	* Selects the front licence plate state (us only).
	*/
	selectFrontLicensePlateState(shouldDisplay: boolean) {
		if (Configuration.isUsRegion()) {
			Utilities.setActiveAll(this.licensePlateUsFront, shouldDisplay);
		}
	}
	
	/**
	* Selects the correct licence plate by region.
	*/
	selectLicensePlateByRegion() {
		Utilities.setActiveAll(this.licensePlate, !Configuration.isUsRegion());
		Utilities.setActiveAll(this.licensePlateUsFront, Configuration.isUsRegion());
		Utilities.setActiveAll(this.licensePlateUsBack, Configuration.isUsRegion());
	}

	/**
	* Selects the soft top light state, displays them by region and via the function argument.
	*/
	selectSoftTopLightState(displayLight: boolean) {
		if (!Configuration.isUsRegion()) {
			Utilities.setActiveAll(this.softTopUpLight, displayLight);
			Utilities.setActiveAll(this.softTopDownLight, displayLight);
		} else {
			Utilities.setActiveAll(this.softTopUpLight, false);
			Utilities.setActiveAll(this.softTopDownLight, false);
		}
	}

	/**
	* display specific fuel cap for US region.
	*/
	selectUsFuelCap() {
		Utilities.setActiveAll(this.fuelCapUsa, Configuration.isUsRegion());
	}

	/**
	* Selects a hood style with the given ID, updating the relevant meshes/materials on the vehicle model.
	*/
	selectHoodStyle(id: string) {

		// hide high brake light in the US.
		if (Configuration.isUsRegion() || this._spareWheelIsActive()) {
			this.selectSoftTopLightState(false);
		}

		// get the hood description for the given ID.
		const hoodDescription = this._hoodDescriptions.find(element => element.colourId === id);
		if (!hoodDescription) {
			console.error('No hood description found for id: ', id);
			return;
		}

		// smoothly fade between the current hood colour and the selected colour.
		this._fadeHoodColour(this._softTopUp4Wheel, hoodDescription.colour);
		this._fadeHoodColour(this._softTopUp5Wheel, hoodDescription.colour);
		this._fadeHoodColour(this._softTopDown4Wheel, hoodDescription.colour);
		this._fadeHoodColour(this._softTopDown5Wheel, hoodDescription.colour);
		this._fadeHoodColour(this._exteriorSideScreen, hoodDescription.colour);
	}

	/**
	* Selects a wing beading style with the given ID, updating the relevant meshes/materials on the vehicle model.
	*/
	selectWingBeadingOption(id: string) {

		// get the wing beading description for the given ID.
		const wingBeadingDescription = this._wingBeadingDescriptions.find(element => element.colourId === id);
		if (!wingBeadingDescription) {
			console.error('No wing beading description found for id: ', id);
			return;
		}

		// smoothly fade between the current wing beading colour and the selected colour.
		this._fadeColour(this._wingBeading, wingBeadingDescription.colour, 'albedoColor' as FadableKey);
	}

	/**
	* Selects a stone guard style with the given ID, updating the relevant meshes/materials on the vehicle model.
	*/
	selectStoneGuardStyle(id: string) {

		// get the stone guard description for the given ID.
		const stoneGuardDescription = this._stoneGuardDescriptions.find(element => element.colourId === id);
		if (!stoneGuardDescription) {
			console.error('No stone guard description found for id: ', id);
			return;
		}

		// smoothly fade between the current stone guard colour and the selected colour.
		this._fadeColour(this._stoneGuard, stoneGuardDescription.colour, 'albedoColor' as FadableKey);
	}

	/**
	* Selects a grille style with the given ID, updating the relevant meshes/materials on the vehicle model.
	*/
	selectGrilleMeshOption(id: string) {

		// get the grille description for the given ID.
		const grilleDescription = this._grilleDescriptions.find(element => element.grilleId === id);
		if (!grilleDescription) {
			console.error('No grille description found for id: ', id);
			return;
		}

		// get the relevant material from the grille description.
		const material = grilleDescription.grilleMaterial as GrilleMaterial;
		if (!material) {
			console.error('No material found for grille description: ', grilleDescription);
			return;
		}

		// get the black grille material.
		const blackGrilleDescription = this._grilleDescriptions.find(element => element.grilleId === Configuration.blackGrilleMeshOption);
		if (!blackGrilleDescription) {
			console.error('No grille description found for id: ', Configuration.blackGrilleMeshOption);
			return;
		}
		const blackMaterial = blackGrilleDescription.grilleMaterial as GrilleMaterial;
		if (!blackMaterial) {
			console.error('No material found for grille description: ', blackGrilleDescription);
			return;
		}

		// set the material on the grille.
		this._grille.material = material;

		// set the colour of the lower grill (matching polished grille if selected, otherwise black).
		if (id === Configuration.polishedGrilleMeshOption) {
			this._fadeColour(this._lowerGrill, material.albedoColor, 'albedoColor' as FadableKey);
		}
		else {
			this._fadeColour(this._lowerGrill, blackMaterial.albedoColor, 'albedoColor' as FadableKey);
		}
	}

	/**
	* Paints the rear half of the splitter (the diffuser) to match the body, when the body paint is changed.
	*/
	selectSplitterBodyPaint(id: string) {
		this._bespokeRearSplitterMaterial = this._selectSplitterPaint(this._splitterDiffuserCarPaintRear, id, Configuration.bespokeBodyPaintColour);
	}

	/**
	* Paints the front half of the splitter to match the wings, when the wing paint is changed. On a single-colour car the wings wear
	* the body paint, so the two halves match; on a two-tone car the front takes the wing colour.
	*/
	selectSplitterWingPaint(id: string) {
		this._bespokeFrontSplitterMaterial = this._selectSplitterPaint(this._splitterDiffuserCarPaint, id, Configuration.bespokeWingPaintColour);
	}

	/**
	* Writes the given body colour, as an rgba array of 0–1 floats, onto the rear half of the splitter while it wears a bespoke colour,
	* so that it keeps up with the colour picker as it is dragged. Does nothing unless a bespoke colour is currently applied.
	* This overwrites the albedo of the material selectSplitterBodyPaint() already built, rather than cloning and tweening a new one,
	* so that it is cheap enough to call on every movement of the picker.
	*/
	updateSplitterBodyColour(colour: NumberArray) {
		this._writeSplitterColour(this._bespokeRearSplitterMaterial, colour);
	}

	/**
	* The front half's twin of updateSplitterBodyColour(), writing the given wing colour onto it while it wears a bespoke colour.
	*/
	updateSplitterWingColour(colour: NumberArray) {
		this._writeSplitterColour(this._bespokeFrontSplitterMaterial, colour);
	}

	/**
	* Selects a splitter style with the given ID, updating the relevant meshes/materials on the vehicle model.
	*/
	selectSplitterStyle(id: string) {

		if (id === Configuration.darkSatinSplitterOption) {
			this._splitterDiffuserCarPaint.active = false;
			this._splitterDiffuserCarPaintRear.active = false;
			this._splitterDiffuserBlack.active = true;
		} else {
			this._splitterDiffuserCarPaint.active = true;
			this._splitterDiffuserCarPaintRear.active = true;
			this._splitterDiffuserBlack.active = false;
		}
	}

	selectDoorGraphicOption(id: string) {

		// hide the door roundel if no option is selected, otherwise show it.
		if (id === 'NONE') {
			this.doorRoundel.active = false;
			return;
		}
		else {
			this.doorRoundel.active = true;
		}

		// get the door roundel description for the given ID.
		const doorRoundelDescription = this._doorRoundelDescriptions.find(element => element.id === id);
		if (!doorRoundelDescription) {
			console.error('No door roundel description found for id: ', id);
			return;
		}

		// set the relevant material.
		this._doorRoundel.material = doorRoundelDescription.material?.clone();
	}
	
	selectAccentStyle(id: string) {

		// initially hide both badge options.
		this.badgeFlagColoured.active = false;
		this.badgeFlagBlack.active = false;
		
		// show the relevant graphic based on the ID.
		switch (id) {
			case AccentOptions.BadgeColoured:
				this.badgeFlagColoured.active = true;
				break;
			case AccentOptions.BadgeBlack:
				this.badgeFlagBlack.active = true;
				break;

			// the accent pack option gives you the b/w badge (and colours the window sides and frames black).
			case AccentOptions.AccentPack:
				this.badgeFlagBlack.active = true;
				break;
		}

		// if accent pack is selected, set the window sides and frames to the accent pack colour.
		if (id === AccentOptions.AccentPack) {
			this._windowSides.material = this.accentPackmaterial?.clone() as CarPbrMaterial;
			this._fadeColour(this._sideWindowFrames, this.accentPackColour, 'albedoColor' as FadableKey);
		}
		else {

			// get a material for the current body paint colour, which handles a bespoke colour having no paint description of its own.
			const material = cloneBodyPaintMaterial(this._paintDescriptions);
			if (!material) {
				return;
			}

			// set the window sides back to the body paint colour.
			this._windowSides.material = material;

			// set the side window frames back to their original chrome colour.
			this._fadeColour(this._sideWindowFrames, this.chromeColour, 'albedoColor' as FadableKey);
		}
	}

	// toggle call signs visibility.
	selectCallSigns(id: string) {
		if (id === 'NONE') {
			this._callSignPilotBadge.active = false;
			this._callSignCopilotBadge.active = false;
			this._callSignPilotText.active = false;
			this._callSignCopilotText.active = false;
		} else {
			this._callSignPilotBadge.active = true;
			this._callSignCopilotBadge.active = true;
			this._callSignPilotText.active = true;
			this._callSignCopilotText.active = true;
		}
	}

	// update call sign text on the car.
	selectCallSignsText(type: string, text: string) {

		if (type === 'pilot') {
			this._callSignPilotText.text = text;
		}
		else if (type === 'copilot') {
			this._callSignCopilotText.text = text;

		}
	}

	//-----------------------------------------------------------------------------------------
	// Private Functions:
	//-----------------------------------------------------------------------------------------

	private _spareWheelIsActive() {
		return Configuration.spareWheelOption !== SpareWheelOptions.None;
	}

	/**
	* Smoothly fades the hood and pipe colour of the material on the given mesh between the current colour and the given colour, using a tween animation.
	*/
	private _fadeHoodColour(mesh: MeshComponent, targetColour: NumberArray) {

		// ensure the given mesh has a material assigned to it.
		if (!mesh.material) {
			console.error('No material has been assigned to the mesh: ', mesh);
			return;
		}

		// clone the current material (as we don't want to change the source material itself).
		const material = mesh.material.clone() as HoodMaterial;
		mesh.material = material;

		// tween the relevant material property to change the colour.
		const colourTween = new Tween({
			hoodColor: material.hoodColor,
			pipeColor: material.pipeColor
		});
		colourTween
			.to(
				{
					hoodColor: targetColour,
					pipeColor: targetColour
				},
				COLOUR_TWEEN_TIME_IN_MS
			)
			.onUpdate((c) => {
				material.hoodColor = c.hoodColor;
				material.pipeColor = c.pipeColor;
			})
			.easing(Easing.Quadratic.InOut);
		this._tweenManager.start(colourTween);
	}
	
	// paints one half of the splitter with the given paint selection - an option id and, for a bespoke id, the colour chosen for it.
	// only the albedo is taken from the paint: the splitter keeps its own material and finish. returns the material when the
	// selection is a bespoke colour, for the caller to cache so that later moves of the picker are a bare albedo write (see
	// _writeSplitterColour()), and null for a standard paint, which is tweened to instead.
	private _selectSplitterPaint(mesh: MeshComponent, id: string, bespokeColour: NumberArray | null): CarPbrMaterial | null {

		// a bespoke colour has no paint description of its own, so its colour is resolved from its paint type's base paint with the
		// chosen colour written in, and put straight on rather than tweened.
		if (Configuration.isBespokePaintColour(id)) {
			const paintMaterial = clonePaintMaterial(this._paintDescriptions, id, bespokeColour);
			if (!paintMaterial) {
				return null;
			}

			// clone the splitter's own material, so that its finish is kept and the shared material is left alone.
			const material = mesh.material?.clone() as CarPbrMaterial;
			if (!material) {
				console.error('No material has been assigned to the mesh: ', mesh);
				return null;
			}
			mesh.material = material;

			// take the colour, and hand the material back so that later moves of the picker are a bare albedo write.
			material.albedoColor = paintMaterial.albedoColor;
			return material;
		}

		// get the paint description for the given ID.
		const paintDescription = this._paintDescriptions.find(element => element.paintId === id);
		if (!paintDescription) {
			console.error('No paint description found for id: ', id);
			return null;
		}

		// get the relevant material from the paint description.
		const material = paintDescription.paintMaterial as CarPbrMaterial;
		if (!material) {
			console.error('No paint material assigned for id: ', id);
			return null;
		}

		// smoothly fade to its colour. a standard paint leaves nothing to cache, which clears any bespoke colour held for this half.
		this._fadeColour(mesh, material.albedoColor, 'albedoColor' as FadableKey);
		return null;
	}

	// overwrites the albedo of a half of the splitter wearing a bespoke colour with the given colour. does nothing when that half is
	// wearing a standard paint, as there is then no cached material to write to.
	private _writeSplitterColour(material: CarPbrMaterial | null, colour: NumberArray) {
		if (!material) {
			return;
		}

		material.albedoColor = [colour[0], colour[1], colour[2], colour[3] ?? 1];
	}

	/**
	* Smoothly fades the colour of any material on the given mesh between the current colour and the given colour, using a tween animation.
	*/
	private _fadeColour(
		mesh: MeshComponent,
		targetColour: NumberArray,
		targetProperty: FadableKey
	) {
		if (!mesh.material) {
			console.error('No material has been assigned to the mesh: ', mesh);
			return;
		}

		const material = mesh.material.clone() as FadableMaterial;
		mesh.material = material;

		const prop = targetProperty as string;

		const colourTween = new Tween({
			[prop]: material[targetProperty],
		});
		colourTween
			.to(
				{
					[prop]: targetColour
				},
				COLOUR_TWEEN_TIME_IN_MS
			)
			.onUpdate((c) => {
				(material as Record<FadableKey, NumberArray>)[targetProperty] = c[prop];
			})
			.easing(Easing.Quadratic.InOut);

		this._tweenManager.start(colourTween);
	}
}
